import React, { useCallback } from 'react';
import { useHistory } from 'react-router';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useToggle, useDeepEqualSelector, useFeature } from 'Shared/utils/hooks';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

// Selectors
import { getWorkspaceTeammate } from 'Shared/selectors/workspace';
import { getDraftFeedbackByWorkspaceUuid } from 'Shared/selectors/Feedbacks.selectors';

// Components
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem, WidthType } from 'Wpds/components/dropdown';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import InviteTeammateModal from 'Shared/components/InviteTeammateModal/InviteTeammateModal';
import { Feature } from 'Shared/types/Organization.type';

function GiveFeedbackAndKudoButton() {
  const history = useHistory();
  const { isGroup, name: workspaceName, id: workspaceId } = useWorkspace();
  const { isActive: isKudosActive } = useFeature(Feature.kudos);
  const dropdownToggleTitle = isKudosActive ? 'Give Feedback & Kudos' : 'Give Feedback';

  const teammate = useDeepEqualSelector(getWorkspaceTeammate(workspaceId));
  const [isInviteTeammateModalOpen, toggleIsInviteTeammateModalOpen] = useToggle(false);

  const draftFeedback = useDeepEqualSelector(getDraftFeedbackByWorkspaceUuid(workspaceId));

  const isNotInvited = () => {
    if (isGroup) return false;
    return Boolean(!teammate);
  };

  const redirectToFeedbackCreation = useCallback(() => {
    if (draftFeedback) {
      history.push(PageUrlsUtil.makeEditFeedbackFormUrl(workspaceId, draftFeedback.uuid));
    } else {
      history.push(PageUrlsUtil.makeFeedbackRequestUrl(workspaceId));
    }
  }, [workspaceId, history, draftFeedback]);

  const handleGiveFeedbackClick = button => {
    if (isNotInvited()) {
      (window as any).analytics.track('Tried to give feedback before inviting user to workspace');
      return toggleIsInviteTeammateModalOpen();
    }
    (window as any).analytics.track('Clicked Feedback/Kudos', {
      button,
      feedbackType: 'feedback',
    });
    return redirectToFeedbackCreation();
  };

  const handleGiveKudoClick = () => {
    history.push(PageUrlsUtil.makeWorkspaceRecognitionRequestNew(workspaceId));
  };

  return (
    <>
      {isKudosActive ? (
        <WpDropdown>
          <WpDropdownToggle>
            <WpButton qaTest="give-feedback-button">
              {dropdownToggleTitle}
              <WpIcon name="angle-down" />
            </WpButton>
          </WpDropdownToggle>
          <WpDropdownMenu width={WidthType.target}>
            <WpDropdownMenuItem
              onClick={() => handleGiveFeedbackClick('dropdown_carrot')}
              qaTest="GiveFeedbackAndKudoButton-give-feedback"
            >
              <WpIcon name="hand-holding-heart" />
              Give Feedback
            </WpDropdownMenuItem>
            <WpDropdownMenuItem onClick={handleGiveKudoClick} qaTest="GiveFeedbackAndKudoButton-give-kudo">
              <WpIcon name="gem" />
              Give Kudos
            </WpDropdownMenuItem>
          </WpDropdownMenu>
        </WpDropdown>
      ) : (
        <WpButton onClick={() => handleGiveFeedbackClick('button')} qaTest="GiveFeedbackAndKudoButton-give-feedback">
          Give Feedback
        </WpButton>
      )}

      <InviteTeammateModal
        isOpen={isInviteTeammateModalOpen}
        toggle={toggleIsInviteTeammateModalOpen}
        workspaceId={workspaceId}
        workspaceTitle={workspaceName}
        alertMessage={`Please invite ${workspaceName} to this workspace to exchange feedback with them.`}
        afterInviteCallback={toggleIsInviteTeammateModalOpen}
      />
    </>
  );
}

export default GiveFeedbackAndKudoButton;
